// words-admin.ts — (B107; planning 10-23 16:45, 18:05; Creative 2's C2.9-1) Admin → Words, pure: the key view as the
// workers answer it, the filters, the edit panel's hints (length, placeholder chips, no newline, the preview) and the
// answer → words mapping. The hints are only hints: the workers' guards decide (the answer is shown word for word).
import { fill } from "./words.ts";

export type WordEdit = { text: string; decidedBy: string; decidedAs: "owner" | "operator"; decidedAt: string; reason: string | null };
export interface WordView {
  key: string; family: string; shows: string; defaultText: string; text: string;
  edit: WordEdit | null; editRefused: string | null;
  placeholders: string[]; maxLength: number; multiline: boolean; lockedMeaning: boolean;
  ownerState: "approved" | "proposed"; state: "approved" | "proposed" | "edited_by_operator";
}
export interface WordHistoryRow {
  id: number; textBefore: string | null; textAfter: string | null; decidedBy: string; decidedAs: "owner" | "operator";
  decidedAt: string; reason: string | null; overApproved: boolean; traceId: string | null;
}
export type WordDetail = WordView & { locale: string; history: WordHistoryRow[] };

export const LOCK_NOTE = "Locked meaning: it can be reworded, never removed. A reason is required.";
/** The group a key sits in, down the left: its family, or "Pages" for the app's own page words (`page.*`). */
export const groupOf = (v: Pick<WordView, "key" | "family">) => (v.key.startsWith("page.") ? "Pages" : v.family);

/** Pure: the groups with their counts, families in the workers' order, Pages last. */
export function groupsOf(words: readonly WordView[]): { group: string; count: number }[] {
  const m = new Map<string, number>();
  for (const v of words) m.set(groupOf(v), (m.get(groupOf(v)) ?? 0) + 1);
  const pages = m.get("Pages");
  m.delete("Pages");
  return [...[...m].map(([group, count]) => ({ group, count })), ...(pages ? [{ group: "Pages", count: pages }] : [])];
}

export interface WordFilter { search: string; group: string | null; edited: boolean; locked: boolean; proposed: boolean }
/** Pure: the rows the filters keep (the search across the key, the default and the current text). */
export function filterWords(words: readonly WordView[], f: WordFilter): WordView[] {
  const q = f.search.trim().toLowerCase();
  return words.filter((v) => (!f.group || groupOf(v) === f.group) && (!f.edited || !!v.edit) && (!f.locked || v.lockedMeaning)
    && (!f.proposed || v.state === "proposed")
    && (!q || v.key.toLowerCase().includes(q) || v.defaultText.toLowerCase().includes(q) || v.text.toLowerCase().includes(q)));
}

/** Pure: "38 / 60", over when past the key's own max — counted as the workers' guard counts it (`text.length`). */
export const lengthOf = (text: string, max: number) => ({ label: `${text.length} / ${max}`, over: text.length > max });

const PLACEHOLDER = /<([a-z][a-z_]{0,31})>/g;   // contracts' words/fill.ts
/** Pure: the key's placeholders as chips (each ticked while it's in the text), and any `<…>` that isn't one of them. */
export function chipsOf(text: string, placeholders: readonly string[]): { chips: { name: string; present: boolean }[]; unknown: string[] } {
  const inText = new Set([...text.matchAll(PLACEHOLDER)].map((m) => m[1]!));
  return { chips: placeholders.map((name) => ({ name, present: inText.has(name) })), unknown: [...inText].filter((n) => !placeholders.includes(n)).sort() };
}

/** Pure: a one-line key takes no newline (Enter does nothing; a pasted one becomes a space). */
export const oneLine = (text: string, multiline: boolean) => (multiline ? text : text.replace(/\r?\n/g, " "));

const SAMPLE: Record<string, string | number> = { n: 5, count: 5, amount: 5, s: 12, m: 4, title: "Your video", clip: "clip 3" };
/** Pure: the preview, the key's placeholders filled with sample values (one with no sample reads as its name; an unknown
 *  `<…>` stays as written). The page draws it as text, so it is escaped exactly as the customer's page draws it. */
export const previewOf = (text: string, placeholders: readonly string[]) =>
  fill(text, Object.fromEntries(placeholders.map((p) => [p, SAMPLE[p] ?? p])));

/** Pure: may the person save? (the reason a locked key needs; the server still decides the rest) */
export function saveOffer(v: Pick<WordView, "lockedMeaning">, text: string, reason: string): { ok: boolean; why?: string } {
  if (!text.trim()) return { ok: false, why: "The words can't be blank." };
  if (v.lockedMeaning && !reason.trim()) return { ok: false, why: "A locked meaning's edit needs a reason." };
  return { ok: true };
}

/** Pure: the owner's-state note above Save. */
export function ownerNote(v: Pick<WordView, "state">, isOwner: boolean): string | null {
  if (isOwner) return "Saving marks it approved.";
  return v.state === "approved" ? "The owner approved this wording. Your edit is recorded as an operator's." : null;
}

export const SAVED = "Saved — new words show within a minute.";
export const RESET_DONE = "Back to the default — it shows within a minute.";
export const NOTHING_TO_RESET = "Nothing to reset: these are already the default words.";
/** Pure: a refused save or reset → the words shown in place (a 400 is the server's own words, its key prefix dropped). */
export function answerText(e: unknown, key: string): string {
  const x = e as { status?: number; message?: string } | null;
  const m = (x?.message ?? "").trim();
  switch (x?.status) {
    case 400: return m.startsWith(`${key}: `) ? m.slice(key.length + 2) : m || "Not saved: the words were refused.";
    case 404: return "This key isn't registered any more.";
    case 501: return "The engine doesn't offer the words store yet — nothing was changed.";
    case 503: return "Not saved: the audit record couldn't be written, so nothing changed. Try again.";
    default: return `Not saved: ${m || "try again"}.`;
  }
}

/** Pure: a read that failed → the words shown in place of the list or the panel. */
export function readErrorText(e: unknown): string {
  const x = e as { status?: number; message?: string } | null;
  if (x?.status === 501) return "The engine doesn't offer the words store yet — nothing was changed.";
  if (x?.status === 404) return "This key isn't registered any more.";
  return (x?.message ?? "").trim() || "try again";
}

/** Pure: one history row's before → after (a reset reads "→ the default"; the first edit "the default →"). */
export const historyLine = (h: Pick<WordHistoryRow, "textBefore" | "textAfter">) =>
  `${h.textBefore === null ? "the default" : `“${h.textBefore}”`} → ${h.textAfter === null ? "the default" : `“${h.textAfter}”`}`;
